import clsx from 'clsx'
import {FC} from 'react'
import {useQuery} from 'react-query'
import {Link} from 'react-router-dom'
import {useIsOnPremises} from '../../auth/core/useOnPremises'
import {hidesPayments} from '../../install-app/core/androidApp'
import {businessSettingsPath} from '../../online-payment/core/businessSettingsPath'
import {
  findFeature,
  getServiceCatalog,
  isPricedTariff,
  serviceCatalogKey,
  ServiceFeatureKey,
} from '../core/catalogApi'
import {
  contactSentence,
  daysLeft,
  fa,
  isContactTariff,
  needsContact,
  RENEW_EARLY_DAYS,
} from '../core/serviceApi'
import {ServiceBadge} from './ServiceBadge'
import {ServiceRequestLink} from './SupportContact'
import {ServiceIntroLink} from './ServiceIntroLink'

type Props = {
  companyId: number
  feature: ServiceFeatureKey
  /** «هر پیش‌فاکتور»، «هر اجرا». */
  each: string
  className?: string
  /** Inside a control (a menu's button): no gem, no link and no «از کیف پول», the short words only. */
  bare?: boolean
}

/**
 * One line with what one use of a value-added service takes for this business now, where the use is started
 * (user decision 2026-10-16): the server's price after the package's free uses, the free list, gifts and codes,
 * «رایگان» when nothing is taken, or that the service must be turned on first. A «تماس بگیرید» service says how support
 * turns it on (with its free allowance and the support line), or «فعال تا …» while the business has an access.
 * Nothing for a free service, a backend without the catalog, or an on-premises install, which has no value-added
 * services.
 */
export const ServiceFeeLine: FC<Props> = ({companyId, feature, each, className, bare = false}) => {
  // No fee is shown on premises, nor in the Bazaar and Myket builds of the Android app.
  const hidden = useIsOnPremises() || hidesPayments()
  const catalog = useQuery(serviceCatalogKey(companyId), () => getServiceCatalog(companyId), {
    enabled: companyId > 0 && !hidden,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  })
  const item = findFeature(catalog.data, feature)
  if (hidden || !item || !isPricedTariff(item.tariff)) return null

  const tariff = item.tariff
  // The price before an offer, a gift or a code, struck through beside what one use takes now (2026-10-18).
  const before = Math.max(tariff.useListAmount ?? 0, tariff.useAmount ?? 0)
  const struck = typeof item.useFee === 'number' && before > item.useFee ? before : undefined
  if (isContactTariff(tariff)) {
    const left = needsContact(tariff) ? undefined : daysLeft(tariff.expiresAt)
    const ending = left !== undefined && left <= RENEW_EARLY_DAYS
    return (
      <span className={clsx('svc-fee-line', className)} role='note'>
        {!bare && <ServiceBadge compact decorative />}
        {needsContact(tariff) ? (
          bare ? (
            <>فعال‌سازی با درخواست</>
          ) : (
            <>
              {tariff.freeQuotaText && <>{tariff.freeQuotaText}؛ </>}
              {contactSentence(tariff)}{' '}
              <ServiceRequestLink
                topic={tariff.expired ? 'AccessRenewal' : 'ServiceActivation'}
                feature={tariff.feature}
                label={tariff.label}
              />
            </>
          )
        ) : (
          <>
            {tariff.expiresAtLabel ? `فعال تا ${tariff.expiresAtLabel}` : 'فعال با دسترسی پشتیبانی'}
            {ending && (
              <>
                ؛ برای تمدید، درخواست ثبت کنید.
                {!bare && (
                  <>
                    {' '}
                    <ServiceRequestLink topic='AccessRenewal' feature={tariff.feature} label={tariff.label} />
                  </>
                )}
              </>
            )}
          </>
        )}
      </span>
    )
  }

  return (
    <span className={clsx('svc-fee-line', className)} role='note'>
      {!bare && <ServiceBadge compact decorative />}
      {!item.active && tariff.chooseMethod ? (
        // «ترکیبی» (2026-10-20): the business picks the subscription or the fee of each use first.
        <>
          روش پرداخت این سرویس هنوز انتخاب نشده است.
          {!bare && (
            <>
              {' '}
              <Link to={businessSettingsPath(companyId, 'services-overview')}>انتخاب روش پرداخت</Link>
            </>
          )}
        </>
      ) : !item.active ? (
        <>
          این سرویس هنوز برای این کسب‌وکار فعال نشده است.
          {!bare && (
            <>
              {' '}
              <Link to={businessSettingsPath(companyId, 'services-overview')}>فعال‌سازی</Link>
            </>
          )}
        </>
      ) : item.useFee === undefined || item.useFee === null ? (
        <>هزینهٔ سرویس {each} طبق تعرفه از کیف پول کسب‌وکار کم می‌شود.</>
      ) : item.useFee > 0 ? (
        <>
          هزینهٔ سرویس {each}: {struck !== undefined && <s className='svc-fee-line__struck'><span className='visually-hidden'>به جای </span>{fa(struck)}</s>}{' '}
          <span className='svc-fee-line__amount'>{fa(item.useFee)} ریال</span>
          {!bare && ' از کیف پول کسب‌وکار'}
        </>
      ) : (
        <>
          هزینهٔ سرویس {each}: {struck !== undefined && <s className='svc-fee-line__struck'><span className='visually-hidden'>به جای </span>{fa(struck)}</s>}{' '}
          <span className='svc-fee-line__amount'>{tariff.method === 'subscription' ? 'رایگان در اشتراک' : 'رایگان'}</span>
        </>
      )}
      {!bare && <ServiceIntroLink feature={feature} label={tariff.label} iconOnly />}
    </span>
  )
}
